import {
  Dispatch,
  FC,
  MouseEvent,
  SetStateAction,
  useRef,
  useState,
} from 'react'
import { Box, Button, LinearProgress, Modal, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Icon, makeStyles } from '@perk-ui/core'
import clsx from 'clsx'
import Dropzone from 'react-dropzone'
import { CheckCircle, File, UploadCloud, X } from 'react-feather'
import { useMutation } from 'react-query'

import request from '../config/axios'

const useStyles = makeStyles(() => ({
  hidden: {
    display: 'none',
  },
}))

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.background.paper,
  padding: '20px 30px',
  borderRadius: theme.shape.borderRadius,
  width: '600px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}))

const UploadSection = styled(Box)(({ theme }) => ({
  border: '2px dashed #E5E7EB',
  borderRadius: '8px',
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '155px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#F9FAFB',
  },
}))

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  gap: '10px',
})

const ImagePreviewContainer = styled(Box)(() => ({
  position: 'relative',
  overflow: 'hidden',
  cursor: 'zoom-in',
  '& img': {
    width: '100%',
    maxHeight: '500px',
    objectFit: 'contain',
    transition: 'transform 0.2s ease',
  },
}))

const UploadBox = styled(Box)<{ isComplete: boolean }>(({ isComplete }) => ({
  background: '#F5F5F8',
  height: '84px',
  borderRadius: '8px',
  padding: '12px',
  gap: '4px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.5s ease, padding-top 0.5s ease',
  ...(isComplete && {
    animation: 'completeFlash 1s ease forwards',
    paddingTop: '32px',
  }),
  '@keyframes completeFlash': {
    '0%': {
      backgroundColor: '#F5F5F8',
    },
    '50%': {
      backgroundColor: '#bcf5c1',
    },
    '100%': {
      backgroundColor: '#E8F5E9',
    },
  },
}))

interface FileUploadModalProps {
  isOpen: boolean
  isFileUploaded: boolean
  setIsFileUploaded: Dispatch<SetStateAction<boolean>>
  onClose: () => void
  surveySlug: string
  treatmentId: string
  treatmentSlug: string
}

const bloodAnalysis = (treatmentSlug: string) => {
  const analysisMap: Record<string, string> = {
    'platelet-lysate': 'Platelet Lysate Analysis',
    prp: 'PRP Blood Analysis',
    'ss-prp': 'SS-PRP Analysis',
    aps: 'APS Analysis',
    ppp: 'PPP Analysis',
  }

  return [
    { type: 'WBA', label: 'Whole Blood Analysis' },
    ...(analysisMap[treatmentSlug]
      ? [{ type: 'OTHERS', label: analysisMap[treatmentSlug] }]
      : []),
  ]
}

interface ApiResponse {
  success: boolean
  results: { WBA: Record<string, string>; OTHERS: Record<string, string> }
  fileKey: { WBA: string | null; OTHERS: string | null }
  presignedUrl: { WBA: string; OTHERS: string }
  error?: string
}

interface ApiError extends Error {
  response?: {
    data: {
      error: string
    }
  }
}

const BLOOD_RESULTS = [
  { title: 'Platelet Count (× 10^3/microL)', key: 'plt' },
  { title: 'Red Blood Cell Count (× 10^3/microL)', key: 'rbc' },
  { title: 'White Blood Cell Count (× 10^3/microL)', key: 'wbc' },
  { title: 'Lymphocyte Count (× 10^3/microL)', key: 'lym#' },
  { title: 'Granulocyte/Neutrophil Count (× 10^3/microL)', key: 'gra#' },
  { title: 'Monocyte Count (× 10^3/microL)', key: 'mon#' },
  { title: 'Additional Info', key: 'more' },
]

type AnalysisType = 'WBA' | 'OTHERS'

const FileUploadModal: FC<FileUploadModalProps> = ({
  isOpen,
  setIsFileUploaded,
  onClose,
  surveySlug,
  treatmentId,
  treatmentSlug,
}) => {
  const classes = useStyles()
  const [state, setState] = useState({
    results: {
      WBA: null as Record<string, string> | null,
      OTHERS: null as Record<string, string> | null,
    },
    fileKey: { WBA: null as string | null, OTHERS: null as string | null },
    presignedUrl: { WBA: '', OTHERS: '' },
    preview: { WBA: false, OTHERS: false },
    currentPreview: null as AnalysisType | null,
    errorMessage: null as string | null,
    uploadedFile: {
      WBA: null as { name: string; size: number } | null,
      OTHERS: null as { name: string; size: number } | null,
    },
    uploadProgress: { WBA: 0, OTHERS: 0 },
  })
  const formDataRef = useRef<FormData | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [isZooming, setIsZooming] = useState(false)
  const imageRef = useRef<HTMLDivElement>(null)

  const uploadMutation = useMutation<ApiResponse, ApiError, FormData>(
    async (formData) => {
      const analysis = formData.get('analysis')
      const response = await request.post('/documents', formData, {
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0
          const current = progressEvent.loaded
          if (total > 0) {
            const simulatedProgress = Math.round((current * 100) / total) / 2
            setState((prev) => ({
              ...prev,
              uploadProgress: {
                ...prev.uploadProgress,
                [analysis as AnalysisType]: simulatedProgress,
              },
            }))
          }
        },
      })
      if (!response.data.success) {
        throw new Error(response.data.error || 'Upload failed')
      }
      return response.data
    },
    {
      onSuccess: (data, variables) => {
        const analysis = variables.get('analysis')
        setState((prev) => ({
          ...prev,
          results: {
            ...prev.results,
            [analysis as AnalysisType]: data.results,
          },
          fileKey: {
            ...prev.fileKey,
            [analysis as AnalysisType]: data.fileKey,
          },
          presignedUrl: {
            ...prev.presignedUrl,
            [analysis as AnalysisType]: data.presignedUrl,
          },
          uploadProgress: {
            ...prev.uploadProgress,
            [analysis as AnalysisType]: 100,
          },
        }))
        setTimeout(() => {
          setState((prev) => ({
            ...prev,
            uploadProgress: {
              ...prev.uploadProgress,
              [analysis as AnalysisType]: 101,
            },
          }))
        }, 1000)
      },
      onError: (error, variables) => {
        const analysis = variables.get('analysis')
        console.error('Upload error:', error.message)
        setState((prev) => ({
          ...prev,
          errorMessage: error.response?.data.error || null,
          uploadedFile: {
            ...prev.uploadedFile,
            [analysis as AnalysisType]: null,
          },
          uploadProgress: {
            ...prev.uploadProgress,
            [analysis as AnalysisType]: 0,
          },
        }))
      },
    },
  )

  const handleFileUpload = async (acceptedFiles: File[], analysis: string) => {
    setState((prev) => ({ ...prev, errorMessage: null }))
    const file = acceptedFiles?.[0]
    if (!file) return

    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]
    if (!validTypes.includes(file.type)) {
      setState((prev) => ({
        ...prev,
        errorMessage:
          'Invalid file type. Please upload a .jpg, .jpeg, .png, or .pdf file.',
      }))
      return
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('doc_type', 'horiba')
    if (analysis) {
      formData.append('analysis', analysis)
    }
    formDataRef.current = formData

    setState((prev) => ({
      ...prev,
      uploadedFile: {
        ...prev.uploadedFile,
        [analysis as AnalysisType]: { name: file.name, size: file.size },
      },
    }))
    uploadMutation.mutate(formData)
  }

  const handleNextClick = () => {
    const currentPreview = state.uploadedFile.WBA ? 'WBA' : 'OTHERS'
    setState((prev) => ({ ...prev, currentPreview }))
    setIsFileUploaded(true)
  }

  const handleSubmitResults = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault()
    const analysis = event.currentTarget.dataset.analysis
    const formData = new FormData(event.currentTarget)
    const updatedResults = Object.fromEntries(formData)

    const formattedResults = BLOOD_RESULTS.reduce((acc, { key }) => {
      acc[key.toUpperCase()] = updatedResults[key] as string
      return acc
    }, {} as Record<string, string>)

    // Additional fields result:
    try {
      const response = await request.post(
        `/documents/process_document_results`,
        {
          fileKey: state.fileKey[analysis as AnalysisType],
          documentResults: formattedResults,
          surveySlug,
          treatmentId,
          analysis: analysis as AnalysisType,
        },
      )

      if (response.data.success) {
        let currentPreview = ''
        if (
          analysis === 'WBA' &&
          !state.preview['OTHERS'] &&
          state.uploadedFile['OTHERS']
        ) {
          currentPreview = 'OTHERS'
        } else if (
          analysis === 'OTHERS' &&
          !state.preview['WBA'] &&
          state.uploadedFile['WBA']
        ) {
          currentPreview = 'WBA'
        }

        setState((prev) => ({
          ...prev,
          preview: {
            ...prev.preview,
            [analysis as AnalysisType]: true,
          },
          currentPreview: currentPreview as AnalysisType,
          errorMessage: null,
        }))

        if (!currentPreview) {
          onClose()
        }
      } else {
        throw new Error(response.data.error || 'Error updating results')
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errorMessage: (error as ApiError).response?.data.error || null,
      }))
    }
  }

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width) * 100
      const y = ((event.clientY - rect.top) / rect.height) * 100
      setMousePosition({ x, y })
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="upload-modal-title">
      <ModalBox>
        {state.currentPreview ? (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontFamily: 'Roboto | sans-serif',
                  fontWeight: 700,
                  fontSize: '32px',
                  lineHeight: '40px',
                  letterSpacing: '0px',
                  textAlign: 'center',
                }}
              >
                Review Information
              </Typography>
              <Button size="small" variant="text" onClick={onClose}>
                <Icon size="medium" color="secondaryText">
                  <X />
                </Icon>
              </Button>
            </Box>
            {state.errorMessage && (
              <Typography
                variant="body2"
                sx={{
                  color: 'red',
                  marginTop: '8px',
                }}
              >
                {state.errorMessage}
              </Typography>
            )}

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between' }}
              key={state.currentPreview}
            >
              <Box sx={{ width: '48%' }}>
                <Typography
                  variant="h6"
                  style={{
                    fontFamily: 'DM Sans',
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0px',
                  }}
                >
                  {
                    bloodAnalysis(treatmentSlug).find(
                      (analysis) => analysis.type === state.currentPreview,
                    )?.label
                  }
                </Typography>
                <ImagePreviewContainer
                  ref={imageRef}
                  onMouseMove={handleMouseMove}
                  onMouseEnter={() => setIsZooming(true)}
                  onMouseLeave={() => setIsZooming(false)}
                >
                  <img
                    src={
                      state.presignedUrl[state.currentPreview as AnalysisType]
                    }
                    alt="Document Preview"
                    style={{
                      transform: isZooming ? 'scale(2)' : 'scale(1)',
                      transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
                    }}
                  />
                </ImagePreviewContainer>
              </Box>
              <Box sx={{ width: '48%' }}>
                {state.results[state.currentPreview as AnalysisType] ? (
                  <form
                    onSubmit={handleSubmitResults}
                    data-analysis={state.currentPreview}
                  >
                    {BLOOD_RESULTS.map(({ title, key }) => {
                      const resultPreview =
                        state.results[state.currentPreview as AnalysisType]
                      let resultValue = resultPreview?.[key]
                      if (key === 'more') {
                        const moreObject = {
                          bloodValues: {
                            WBC: resultPreview?.['wbc'],
                            RBC: resultPreview?.['rbc'],
                            HGB: resultPreview?.['hgb'],
                            HCT: resultPreview?.['hct'],
                            MCV: resultPreview?.['mcv'],
                            MCH: resultPreview?.['mch'],
                            MCHC: resultPreview?.['mchc'],
                            RDW: resultPreview?.['rdw'],
                            PLT: resultPreview?.['plt'],
                            MPV: resultPreview?.['mpv'],
                            'LYM%': resultPreview?.['lym%'],
                            'LYM#': resultPreview?.['lym#'],
                            'MON%': resultPreview?.['mon%'],
                            'MON#': resultPreview?.['mon#'],
                            'GRA%': resultPreview?.['gra%'],
                            'GRA#': resultPreview?.['gra#'],
                          },
                          additionalValues: {
                            sampleId: resultPreview?.['Sample ID'],
                            dob: resultPreview?.['dob'],
                            physician: resultPreview?.['physician'],
                            analysisDate: resultPreview?.['analysis date'],
                          },
                        }
                        resultValue = JSON.stringify(moreObject)
                      }
                      return (
                        <div
                          key={key}
                          className={clsx({
                            [classes.hidden]: key === 'more',
                          })}
                        >
                          <label
                            style={{
                              fontSize: '14px',
                              fontWeight: 500,
                            }}
                          >
                            {title}
                          </label>
                          <input
                            type="text"
                            name={key}
                            defaultValue={resultValue}
                            onChange={(e) =>
                              setState({
                                ...state,
                                results: {
                                  ...state.results,
                                  [state.currentPreview as AnalysisType]: {
                                    ...(state.results[
                                      state.currentPreview as AnalysisType
                                    ] || {}),
                                    [key]: e.target.value,
                                  },
                                },
                              })
                            }
                            style={{
                              background: '#F5F5F8',
                              width: '100%',
                              borderRadius: '8px',
                              justifyContent: 'space-between',
                              padding: '14px 14px',
                              border: '0',
                              color: '#767676',
                              margin: '5px 0',
                            }}
                          />
                        </div>
                      )
                    })}
                    <ButtonContainer style={{ marginTop: '16px' }}>
                      <button
                        style={{
                          border: '0',
                          width: '100%',
                          color: '#black',
                          fontWeight: 700,
                          background: 'transparent',
                        }}
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                      <Box
                        component="button"
                        sx={{
                          borderRadius: '16px',
                          padding: '16px 12px',
                          background: '#56AAE8',
                          width: '100%',
                          color: 'white',
                          fontWeight: 700,
                          border: '0',
                          cursor: 'pointer',
                          '&:hover': {
                            background: '#4DA1D8',
                          },
                        }}
                        type="submit"
                      >
                        Approve
                      </Box>
                    </ButtonContainer>
                  </form>
                ) : (
                  <Typography>No results available yet.</Typography>
                )}
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontFamily: 'Roboto | sans-serif',
                  fontWeight: 700,
                  fontSize: '32px',
                  lineHeight: '40px',
                  letterSpacing: '0px',
                  textAlign: 'center',
                }}
              >
                Upload Files
              </Typography>
              <Button size="small" variant="text" onClick={onClose}>
                <Icon size="medium" color="secondaryText">
                  <X />
                </Icon>
              </Button>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Roboto | sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0px',
                color: '#6B6B6B',
              }}
            >
              Please upload one page at a time to ensure accurate processing,
              with each document representing a single test report.
            </Typography>
            {state.errorMessage && (
              <Typography
                variant="body2"
                sx={{
                  color: 'red',
                  marginTop: '8px',
                }}
              >
                {state.errorMessage}
              </Typography>
            )}
            {bloodAnalysis(treatmentSlug).map((analysis) => (
              <Box key={analysis.type}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontFamily: 'DM Sans',
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0px',
                  }}
                >
                  {analysis.label}
                </Typography>
                {!state.uploadedFile[analysis.type as AnalysisType] && (
                  <Dropzone
                    onDrop={(acceptedFiles) =>
                      handleFileUpload(acceptedFiles, analysis.type)
                    }
                  >
                    {({ getRootProps, getInputProps }) => (
                      <UploadSection component="div" {...getRootProps()}>
                        <Box
                          sx={{
                            width: '43.2578125px',
                            height: '43.2578125px',
                            borderRadius: '33.99px',
                            backgroundColor: '#56AAE81A',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '& svg': {
                              stroke: '#56AAE8',
                              strokeWidth: '1.8px',
                            },
                          }}
                        >
                          <UploadCloud />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0px',
                            textAlign: 'center',
                          }}
                        >
                          Upload PDF
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '24px',
                            letterSpacing: '0px',
                            textAlign: 'center',
                            '& a': {
                              color: '#56AAE8',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            },
                          }}
                        >
                          <a>Choose a file</a> or drag them here
                        </Typography>
                        <input
                          id="whole-blood-upload"
                          accept="application/pdf,image/*"
                          {...getInputProps()}
                        />
                      </UploadSection>
                    )}
                  </Dropzone>
                )}
                {state.uploadedFile[analysis.type as AnalysisType] && (
                  <UploadBox
                    isComplete={
                      state.uploadProgress[analysis.type as AnalysisType] ===
                      101
                    }
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'start',
                        width: '100%',
                        gap: '8px',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <File
                          size={33}
                          style={{
                            color: '#737473',
                            borderRadius: '8px',
                            backgroundColor: '#D6D6DD',
                            padding: '6px',
                          }}
                        />
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500 }}>
                            {
                              state.uploadedFile[analysis.type as AnalysisType]
                                ?.name
                            }
                          </div>
                          <div style={{ fontSize: '12px', color: '#767676' }}>
                            {(
                              (state.uploadedFile[analysis.type as AnalysisType]
                                ?.size ?? 0) / 1024
                            ).toFixed(0)}{' '}
                            KB
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          justifyContent: 'center',
                          height: '100%',
                        }}
                      >
                        {state.uploadProgress[analysis.type as AnalysisType] ===
                          101 && (
                          <CheckCircle size={20} style={{ color: '#4CAF50' }} />
                        )}
                        <div
                          onClick={() => {
                            setState({
                              ...state,
                              uploadedFile: {
                                ...state.uploadedFile,
                                [analysis.type as AnalysisType]: null,
                              },
                              uploadProgress: {
                                ...state.uploadProgress,
                                [analysis.type as AnalysisType]: 0,
                              },
                              errorMessage: null,
                            })
                            formDataRef.current?.delete('file')
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <X size={20} style={{ marginTop: '5px' }} />
                        </div>
                      </div>
                    </div>
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '8px',
                        transition: 'opacity 0.5s ease',
                        opacity:
                          state.uploadProgress[
                            analysis.type as AnalysisType
                          ] === 101
                            ? 0
                            : 1,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={
                          state.uploadProgress[analysis.type as AnalysisType]
                        }
                        sx={{ flexGrow: 1 }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: '8px',
                          fontSize: '12px',
                          color: '#737473',
                        }}
                      >
                        {state.uploadProgress[analysis.type as AnalysisType]}%
                      </div>
                    </Box>
                  </UploadBox>
                )}
              </Box>
            ))}

            <ButtonContainer>
              <button
                style={{
                  border: '0',
                  width: '100%',
                  color: '#black',
                  fontWeight: 700,
                  background: 'transparent',
                }}
                onClick={onClose}
                disabled={uploadMutation.isLoading}
              >
                Cancel
              </button>
              <button
                style={{
                  borderRadius: '16px',
                  padding: '16px 12px',
                  background:
                    (!state.uploadedFile.WBA && !state.uploadedFile.OTHERS) ||
                    uploadMutation.isLoading
                      ? '#F1F1F1'
                      : '#56AAE8',
                  width: '100%',
                  color:
                    (!state.uploadedFile.WBA && !state.uploadedFile.OTHERS) ||
                    uploadMutation.isLoading
                      ? '#A1A1A1'
                      : 'white',
                  fontWeight: 700,
                  border: '0',
                }}
                onClick={handleNextClick}
                disabled={
                  (!state.uploadedFile.WBA && !state.uploadedFile.OTHERS) ||
                  uploadMutation.isLoading
                }
              >
                Next
              </button>
            </ButtonContainer>
          </>
        )}
      </ModalBox>
    </Modal>
  )
}

export default FileUploadModal
